import { useEffect, useState } from 'react';
import {
  DEFAULT_RATIO_GUARD_PROFILES,
  RATIO_GUARD_PROFILES,
  analyzeRatioGuard,
  calculateLockedResolution,
  simplifyAspectRatio,
  type AspectRatio,
  type RatioGuardProfileGroup,
  type RatioGuardProfileId,
  type ResolutionCandidate,
} from '../ratioGuard';
import type { Resolution } from '../types';

const MAX_DIMENSION = 1_000_000;
const STORAGE_KEY = 'screengrid-ratio-guard';
const PROFILE_GROUPS: readonly RatioGuardProfileGroup[] = [
  'Live playback',
  'Transport',
  'Post-production',
  'Delivery',
];

interface StoredRatioGuardState {
  resolution: Resolution;
  locked: boolean;
  aspect: AspectRatio;
  selectedProfiles: RatioGuardProfileId[];
}

export function RatioGuard() {
  const [initial] = useState(readStoredState);
  const [resolution, setResolution] = useState(initial.resolution);
  const [widthDraft, setWidthDraft] = useState(String(initial.resolution.width));
  const [heightDraft, setHeightDraft] = useState(String(initial.resolution.height));
  const [locked, setLocked] = useState(initial.locked);
  const [lockedAspect, setLockedAspect] = useState(initial.aspect);
  const [selectedProfiles, setSelectedProfiles] = useState<RatioGuardProfileId[]>(
    initial.selectedProfiles,
  );
  const [gpuTextureLimit] = useState(readGpuTextureLimit);

  const currentAspect = locked ? lockedAspect : simplifyAspectRatio(resolution);
  const analysis = analyzeRatioGuard(resolution, selectedProfiles, currentAspect, gpuTextureLimit);
  const issueCount = analysis.profileResults.filter((result) => result.status !== 'pass').length;

  useEffect(() => {
    const stored: StoredRatioGuardState = {
      resolution,
      locked,
      aspect: lockedAspect,
      selectedProfiles,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // RatioGuard remains fully usable when browser storage is unavailable.
    }
  }, [resolution, locked, lockedAspect, selectedProfiles]);

  const updateDimension = (leading: 'width' | 'height', draft: string) => {
    if (leading === 'width') setWidthDraft(draft);
    else setHeightDraft(draft);
    const value = parseDimension(draft);
    if (value === null) return;

    const next = locked
      ? calculateLockedResolution(leading, value, lockedAspect)
      : { ...resolution, [leading]: value };
    setResolution(next);
    if (leading === 'width' && locked) setHeightDraft(String(next.height));
    if (leading === 'height' && locked) setWidthDraft(String(next.width));
  };

  const toggleLock = () => {
    if (!locked) setLockedAspect(simplifyAspectRatio(resolution));
    setLocked((value) => !value);
  };

  const toggleProfile = (profileId: RatioGuardProfileId) => {
    setSelectedProfiles((current) =>
      current.includes(profileId)
        ? current.filter((id) => id !== profileId)
        : [...current, profileId],
    );
  };

  const applyCandidate = (candidate: ResolutionCandidate | null) => {
    if (!candidate) return;
    const next = candidate.resolution;
    setResolution(next);
    setWidthDraft(String(next.width));
    setHeightDraft(String(next.height));
    if (locked) setLockedAspect(simplifyAspectRatio(next));
  };

  const applyPreset = (next: Resolution) => {
    setResolution(next);
    setWidthDraft(String(next.width));
    setHeightDraft(String(next.height));
    if (locked) setLockedAspect(simplifyAspectRatio(next));
  };

  return (
    <main className="ratio-guard-page">
      <div className="ratio-guard-content">
        <header className="ratio-guard-hero">
          <div>
            <p className="ratio-guard-kicker">RASTER CALCULATOR / AV COMPATIBILITY</p>
            <h1>RatioGuard</h1>
            <p>
              Lock a content aspect ratio, resize from either dimension, and find the closest
              codec-friendly output raster before export or playback.
            </p>
          </div>
          <div className="ratio-guard-health" data-state={issueCount ? 'warning' : 'pass'}>
            <span>{issueCount ? 'CHECK RESULT' : 'READY'}</span>
            <strong>
              {issueCount
                ? `${issueCount} profile${issueCount === 1 ? '' : 's'} need attention`
                : 'Selected profiles pass'}
            </strong>
            <small>
              {selectedProfiles.length} of {RATIO_GUARD_PROFILES.length} profiles selected
            </small>
          </div>
        </header>

        <section className="ratio-guard-grid" aria-label="RatioGuard controls">
          <div className="ratio-card ratio-card--input">
            <div className="ratio-card-heading">
              <div>
                <span>01 / TARGET</span>
                <h2>Content resolution</h2>
              </div>
              <button
                type="button"
                className={`ratio-lock ${locked ? 'is-active' : ''}`}
                aria-pressed={locked}
                onClick={toggleLock}
              >
                <span aria-hidden="true">{locked ? '◆' : '◇'}</span>
                {locked ? 'Aspect locked' : 'Lock aspect'}
              </button>
            </div>

            <div className="ratio-dimensions">
              <label>
                <span>Width</span>
                <span className="ratio-input-wrap">
                  <input
                    type="number"
                    min="1"
                    max={MAX_DIMENSION}
                    step="1"
                    value={widthDraft}
                    aria-invalid={parseDimension(widthDraft) === null}
                    onChange={(event) => updateDimension('width', event.target.value)}
                    onBlur={() => setWidthDraft(String(resolution.width))}
                  />
                  <small>px</small>
                </span>
              </label>
              <span className="ratio-dimensions-separator" aria-hidden="true">
                ×
              </span>
              <label>
                <span>Height</span>
                <span className="ratio-input-wrap">
                  <input
                    type="number"
                    min="1"
                    max={MAX_DIMENSION}
                    step="1"
                    value={heightDraft}
                    aria-invalid={parseDimension(heightDraft) === null}
                    onChange={(event) => updateDimension('height', event.target.value)}
                    onBlur={() => setHeightDraft(String(resolution.height))}
                  />
                  <small>px</small>
                </span>
              </label>
            </div>

            <div className="ratio-readout">
              <div>
                <span>Aspect</span>
                <strong>
                  {currentAspect.width}:{currentAspect.height}
                </strong>
              </div>
              <div>
                <span>Decimal</span>
                <strong>{(currentAspect.width / currentAspect.height).toFixed(4)}</strong>
              </div>
              <div>
                <span>GPU texture</span>
                <strong>
                  {gpuTextureLimit ? `${formatNumber(gpuTextureLimit)} px` : 'Unavailable'}
                </strong>
              </div>
            </div>

            <div className="ratio-presets" aria-label="Resolution presets">
              <button type="button" onClick={() => applyPreset({ width: 1920, height: 1080 })}>
                1920 × 1080
              </button>
              <button type="button" onClick={() => applyPreset({ width: 3840, height: 2160 })}>
                3840 × 2160
              </button>
              <button type="button" onClick={() => applyPreset({ width: 10_989, height: 1200 })}>
                10989 × 1200
              </button>
            </div>
          </div>

          <div className="ratio-card ratio-card--profiles">
            <div className="ratio-card-heading">
              <div>
                <span>02 / PIPELINE</span>
                <h2>Compatibility profiles</h2>
              </div>
              <div className="ratio-profile-actions">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedProfiles(RATIO_GUARD_PROFILES.map((profile) => profile.id))
                  }
                >
                  All
                </button>
                <button type="button" onClick={() => setSelectedProfiles([])}>
                  None
                </button>
              </div>
            </div>

            <div className="ratio-profile-groups">
              {PROFILE_GROUPS.map((group) => (
                <fieldset key={group}>
                  <legend>{group}</legend>
                  {RATIO_GUARD_PROFILES.filter((profile) => profile.group === group).map(
                    (profile) => (
                      <label key={profile.id} className="ratio-profile-option">
                        <input
                          type="checkbox"
                          checked={selectedProfiles.includes(profile.id)}
                          onChange={() => toggleProfile(profile.id)}
                        />
                        <span aria-hidden="true" />
                        <span>
                          <strong>{profile.label}</strong>
                          <small>{profile.requirement}</small>
                        </span>
                      </label>
                    ),
                  )}
                </fieldset>
              ))}
            </div>
          </div>
        </section>

        <section className="ratio-summary" aria-labelledby="ratio-summary-title">
          <div className="ratio-summary-primary">
            <span>03 / RECOMMENDED OUTPUT</span>
            <h2 id="ratio-summary-title">
              {analysis.recommended
                ? formatResolution(analysis.recommended.resolution)
                : 'No compatible output'}
            </h2>
            <p>
              {analysis.recommended
                ? recommendationText(analysis.recommended, resolution)
                : 'The selected maximum-dimension limits do not allow an aligned output.'}
            </p>
            <button
              type="button"
              className="button button--primary"
              disabled={!analysis.recommended || isSameResolution(analysis.recommended, resolution)}
              onClick={() => applyCandidate(analysis.recommended)}
            >
              {analysis.recommended && isSameResolution(analysis.recommended, resolution)
                ? 'Already applied'
                : 'Apply recommended'}
            </button>
          </div>
          <div className="ratio-summary-specs">
            <SummarySpec
              label="Combined alignment"
              value={`${analysis.recommendedWidthMultiple} × ${analysis.recommendedHeightMultiple} px`}
            />
            <SummarySpec
              label="Hard requirement"
              value={`${analysis.requiredWidthMultiple} × ${analysis.requiredHeightMultiple} px`}
            />
            <SummarySpec
              label="Effective max"
              value={
                analysis.maximumWidth
                  ? `${formatNumber(analysis.maximumWidth)} × ${formatNumber(analysis.maximumHeight!)} px`
                  : 'Profile-specific'
              }
            />
            <SummarySpec
              label="Aspect deviation"
              value={formatDeviation(analysis.recommended?.aspectDeviationPercent)}
            />
          </div>
        </section>

        <section className="ratio-alternatives" aria-labelledby="ratio-alternatives-title">
          <div className="ratio-section-heading">
            <div>
              <span>04 / ADJUSTMENT MODES</span>
              <h2 id="ratio-alternatives-title">Choose how to fit the raster</h2>
            </div>
            <p>Padding is preferred because it preserves all source pixels.</p>
          </div>
          <div className="ratio-alternative-grid">
            <CandidateCard
              title="Pad / round up"
              description="Adds edge pixels; no source content is removed."
              candidate={analysis.pad}
              target={resolution}
              recommended
              onApply={applyCandidate}
            />
            <CandidateCard
              title="Crop / round down"
              description="Removes edge pixels to stay at or below the target."
              candidate={analysis.crop}
              target={resolution}
              onApply={applyCandidate}
            />
            <CandidateCard
              title="Exact aspect"
              description="Preserves the ratio exactly; the size change can be much larger."
              candidate={analysis.exactAspect}
              target={resolution}
              onApply={applyCandidate}
            />
          </div>
        </section>

        <section className="ratio-results" aria-labelledby="ratio-results-title">
          <div className="ratio-section-heading">
            <div>
              <span>05 / PROFILE REPORT</span>
              <h2 id="ratio-results-title">Compatibility summary</h2>
            </div>
            <p>Geometry guidance only — encoding performance and bitrate are not estimated.</p>
          </div>
          <div className="ratio-table-wrap">
            <table className="ratio-table">
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>Status</th>
                  <th>Check</th>
                  <th>Closest raster</th>
                  <th>Delta</th>
                  <th aria-label="Action" />
                </tr>
              </thead>
              <tbody>
                {analysis.profileResults.length ? (
                  analysis.profileResults.map((result) => (
                    <tr key={result.profile.id}>
                      <td>
                        <strong>{result.profile.shortLabel}</strong>
                        <small>{result.profile.group}</small>
                      </td>
                      <td>
                        <span className={`ratio-status ratio-status--${result.status}`}>
                          {result.status}
                        </span>
                      </td>
                      <td>
                        {result.issues.length ? (
                          <ul>
                            {result.issues.map((issue) => (
                              <li key={`${issue.kind}-${issue.message}`}>{issue.message}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="ratio-table-pass">Geometry check passed.</span>
                        )}
                        <small>{result.profile.description}</small>
                      </td>
                      <td>
                        <strong>
                          {result.closest ? formatResolution(result.closest.resolution) : '—'}
                        </strong>
                      </td>
                      <td>{result.closest ? formatDelta(result.closest) : '—'}</td>
                      <td>
                        <button
                          type="button"
                          disabled={!result.closest || isSameResolution(result.closest, resolution)}
                          onClick={() => applyCandidate(result.closest)}
                        >
                          Apply
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="ratio-table-empty">
                      Select at least one compatibility profile to run checks.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="ratio-disclaimer">
            Results are conservative preflight guidance. Hardware encoders, decoders, NDI devices,
            media servers, and host applications can impose additional limits; test the final file
            on the actual playback chain.
          </p>
        </section>
      </div>
    </main>
  );
}

interface SummarySpecProps {
  label: string;
  value: string;
}

function SummarySpec({ label, value }: SummarySpecProps) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

interface CandidateCardProps {
  title: string;
  description: string;
  candidate: ResolutionCandidate | null;
  target: Resolution;
  recommended?: boolean;
  onApply: (candidate: ResolutionCandidate | null) => void;
}

function CandidateCard({
  title,
  description,
  candidate,
  target,
  recommended = false,
  onApply,
}: CandidateCardProps) {
  return (
    <article className={`ratio-candidate ${recommended ? 'is-recommended' : ''}`}>
      <header>
        <span>{title}</span>
        {recommended && <small>DEFAULT</small>}
      </header>
      <strong>{candidate ? formatResolution(candidate.resolution) : 'Not available'}</strong>
      <p>{description}</p>
      {candidate && (
        <dl>
          <div>
            <dt>Delta</dt>
            <dd>{formatDelta(candidate)}</dd>
          </div>
          <div>
            <dt>Aspect drift</dt>
            <dd>{formatDeviation(candidate.aspectDeviationPercent)}</dd>
          </div>
        </dl>
      )}
      <button
        type="button"
        className="button"
        disabled={!candidate || isSameResolution(candidate, target)}
        onClick={() => onApply(candidate)}
      >
        {candidate && isSameResolution(candidate, target) ? 'Current raster' : 'Apply'}
      </button>
    </article>
  );
}

function readStoredState(): StoredRatioGuardState {
  const fallback: StoredRatioGuardState = {
    resolution: { width: 1920, height: 1080 },
    locked: false,
    aspect: { width: 16, height: 9 },
    selectedProfiles: [...DEFAULT_RATIO_GUARD_PROFILES],
  };
  try {
    const parsed = JSON.parse(
      localStorage.getItem(STORAGE_KEY) ?? '',
    ) as Partial<StoredRatioGuardState>;
    const width = parseDimension(String(parsed.resolution?.width));
    const height = parseDimension(String(parsed.resolution?.height));
    const selectedProfiles = Array.isArray(parsed.selectedProfiles)
      ? parsed.selectedProfiles.filter((id): id is RatioGuardProfileId =>
          RATIO_GUARD_PROFILES.some((profile) => profile.id === id),
        )
      : fallback.selectedProfiles;
    if (width === null || height === null) return fallback;
    const resolution = { width, height };
    return {
      resolution,
      locked: Boolean(parsed.locked),
      aspect:
        parsed.aspect &&
        parseDimension(String(parsed.aspect.width)) !== null &&
        parseDimension(String(parsed.aspect.height)) !== null
          ? { width: parsed.aspect.width!, height: parsed.aspect.height! }
          : simplifyAspectRatio(resolution),
      selectedProfiles,
    };
  } catch {
    return fallback;
  }
}

function readGpuTextureLimit(): number | null {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
  return context ? (context.getParameter(context.MAX_TEXTURE_SIZE) as number) : null;
}

function parseDimension(value: string): number | null {
  if (!/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= MAX_DIMENSION ? parsed : null;
}

function formatResolution(resolution: Resolution): string {
  return `${formatNumber(resolution.width)} × ${formatNumber(resolution.height)} px`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

function formatDelta(candidate: ResolutionCandidate): string {
  return `${formatSigned(candidate.deltaWidth)} W / ${formatSigned(candidate.deltaHeight)} H`;
}

function formatSigned(value: number): string {
  if (value === 0) return '±0';
  return `${value > 0 ? '+' : '−'}${formatNumber(Math.abs(value))}`;
}

function formatDeviation(value: number | undefined): string {
  if (value === undefined) return '—';
  if (value < 0.00005) return '0% (exact)';
  return `${value.toFixed(value < 0.01 ? 4 : 2)}%`;
}

function isSameResolution(candidate: ResolutionCandidate, target: Resolution): boolean {
  return (
    candidate.resolution.width === target.width && candidate.resolution.height === target.height
  );
}

function recommendationText(candidate: ResolutionCandidate, target: Resolution): string {
  if (isSameResolution(candidate, target)) {
    return 'The current raster already satisfies the combined selected profile geometry.';
  }
  return `${formatDelta(candidate)} from target · ${formatDeviation(candidate.aspectDeviationPercent)} aspect deviation.`;
}
